import { CircleCheckIcon, EllipsisVerticalIcon, PinIcon, PinOffIcon, Trash2Icon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DraftIcon from '../../../assets/icons/draft-icon.tsx';
import { Button } from '../../../components/ui/button.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import type { RouterOutputs } from '../../../utils/api.ts';
import { Can } from '../../acl/can.tsx';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import { DraftOrderCreateDialog } from '../../sales-draft/components/draft-order-create-dialog.tsx';
import { TraskCreateDialog } from '../../tasks/components/task-create-dialog.tsx';
import { usePinNote } from '../hooks/use-pin-note';
import { useUnpinNote } from '../hooks/use-unpin-note';
import { NoteDeleteDialog } from './note-delete-dialog';

type NoteOptionsDropdownProps = {
    note: RouterOutputs['platoUser']['query']['notes']['getNoteById'];
    onDelete?: () => void;
};

export const NoteOptionsDropdown = ({ note, onDelete }: NoteOptionsDropdownProps) => {
    const { t } = useLocale('common');
    const navigate = useNavigate();

    const { pin } = usePinNote(note);
    const { unpin } = useUnpinNote(note);

    const handleNewTaskClick = () => {
        DialogStateManager.show(TraskCreateDialog, {
            links: note.customer
                ? [
                      {
                          modelType: SearchEntity.Customer,
                          modelId: note.customer.customerId,
                          title: note.customer?.customerName || 'Customer',
                      },
                  ]
                : undefined,
        });
    };

    const handleNewTransactionClick = () => {
        DialogStateManager.show(DraftOrderCreateDialog, {
            onSuccess: (data) => {
                navigate(`/drafts/${data.draft_order.draft_order_id}/line-items`);
            },
            customerId: note.customer?.customerId ?? undefined,
        });
    };

    const handleDeleteClick = () => {
        DialogStateManager.show(NoteDeleteDialog, {
            noteId: note.noteId,
            noteType: note.noteType,
            onDeleted: onDelete,
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="icon" icon={EllipsisVerticalIcon} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom">
                {note.noteType === 'NOTE' && !note.pinnedAt && (
                    <DropdownMenuItem onSelect={pin}>
                        <PinIcon className="h-4 w-4" />
                        <Text as="span">{t('notes__pin_note', { ns: 'notes' })}</Text>
                    </DropdownMenuItem>
                )}
                {note.noteType === 'NOTE' && note.pinnedAt && (
                    <DropdownMenuItem onSelect={unpin}>
                        <PinOffIcon className="h-4 w-4" />
                        <Text as="span">{t('notes__unpin_note', { ns: 'notes' })}</Text>
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={handleNewTaskClick}>
                    <CircleCheckIcon className="h-4 w-4" />
                    <Text as="span">{t('top_navbar__new_task')}</Text>
                </DropdownMenuItem>
                <Can I="create" a="SalesDraft">
                    <DropdownMenuItem onSelect={handleNewTransactionClick}>
                        <DraftIcon className="h-4 w-4" />
                        <Text as="span" className="ml-2">
                            {t('sales_draft__new_form___title')}
                        </Text>
                    </DropdownMenuItem>
                </Can>
                <DropdownMenuItem onSelect={handleDeleteClick}>
                    <Trash2Icon className="h-4 w-4 text-red-700" />
                    <Text as="span" color="red-600" className="ml-2">
                        {t('form__delete')}
                    </Text>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
